import {
	List,
	ListItem,
	ListItemText,
	Divider,
	ListItemIcon,
	Avatar,
	Paper,
	CircularProgress,
	Box,
	Typography,
	Pagination,
	Button,
	Checkbox,
	IconButton,
	Tabs,
	Tab,
} from '@mui/material'
import React, { useState, memo } from 'react'
import ApprovalIcon from '@mui/icons-material/Approval'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MenuIcon from '@mui/icons-material/Menu'
import HistoryIcon from '@mui/icons-material/History'
import PendingIcon from '@mui/icons-material/Pending'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import dayjs from 'dayjs'
import AssignmentIcon from '@mui/icons-material/Assignment'
import AssignToDialog from '../core/AssignToDialog'
import { Survey, SurveyFilters } from '../../types/Work/Survey'
import { useTokenClaims } from '../../security/useTokenClaims'
import { RequestWorker } from '../../types/Work/Request'
import { assignSurveys } from '../../services/quantitySurveyService'

const SurveyListItemComponent = ({
	survey,
	selectMode,
	isSelected,
	onSelect,
	projectTimeZone,
	selectedSurveyId,
	onSurveyClick,
}: {
	survey: Survey
	selectMode: boolean
	isSelected: boolean
	onSelect: (id: number) => void
	projectTimeZone: string | undefined
	selectedSurveyId: number | null
	onSurveyClick: (surveyId: number) => void
}) => {
	return (
		<ListItem
			sx={{
				'&:hover': {
					backgroundColor: selectedSurveyId === survey.id ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.04)',
					color: 'inherit !important',
				},
				'& .MuiListItemText-primary': {
					color: 'inherit !important',
					wordBreak: 'break-word',
					whiteSpace: 'normal',
				},
				'& .MuiListItemText-secondary': {
					wordBreak: 'break-word',
					whiteSpace: 'normal',
				},
				'& .MuiListItemIcon-root': {
					color: 'inherit !important',
					minWidth: '40px',
				},
				width: '100%',
				backgroundColor: selectedSurveyId === survey.id ? 'rgba(25, 118, 210, 0.15)' : '#ffffff',
				padding: '8px 16px',
				marginBottom: '4px',
				borderRadius: '4px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				cursor: 'pointer',
				minHeight: {
					xs: '5rem',
					sm: '5.5rem',
					md: '6rem',
				},
				boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
			}}
			onClick={() => {
				if (selectMode) {
					onSelect(survey.id)
				} else {
					onSurveyClick(survey.id)
				}
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: 15,
					minWidth: 0,
					flex: 1,
				}}
			>
				{selectMode && (
					<Checkbox
						checked={isSelected}
						onChange={() => onSelect(survey.id)}
						onClick={(e) => e.stopPropagation()}
					/>
				)}
				<ListItemIcon sx={{ flexShrink: 0, pt: 1 }}>
					<Avatar sx={{ bgcolor: 'primary.main' }}>
						<ApprovalIcon />
					</Avatar>
				</ListItemIcon>
				<ListItemText
					primary={survey.workItemPath}
					secondary={
						<React.Fragment>
							<Typography component='span' variant='body2' color='text.primary'>
								Assigned to: {survey.assignedTo}
							</Typography>
							<br />
							<Typography
								component='span'
								variant='body2'
								color={
									survey.status === 'Unassigned'
										? 'error'
										: survey.status === 'Pending'
										? 'warning.main'
										: survey.status === 'Surveyed'
										? 'success.main'
										: 'text.secondary'
								}
							>
								Status: {survey.status}
							</Typography>
							<br />
							<Typography component='span' variant='body2' color='text.secondary'>
								Requested On:{' '}
								{dayjs.utc(survey.requestedOn).tz(projectTimeZone).format('MM/DD/YYYY')}
							</Typography>
							<br />
							{survey.assignedOn && (
								<Typography component='span' variant='body2' color='text.secondary'>
									Assigned On:{' '}
									{dayjs.utc(survey.assignedOn).tz(projectTimeZone).format('MM/DD/YYYY')}
								</Typography>
							)}
						</React.Fragment>
					}
				/>
			</div>
			<ChevronRightIcon sx={{ color: 'text.secondary' }} />
		</ListItem>
	)
}

SurveyListItemComponent.displayName = 'SurveyListItem'
const SurveyListItem = memo(SurveyListItemComponent)

type SurveysListProps = {
	isExpanded: boolean
	onToggleExpand: () => void
	selectedSurveyId: number | null
	onSurveyClick: (surveyId: number) => void
	filters: SurveyFilters
	setFilters: React.Dispatch<React.SetStateAction<SurveyFilters>>
	assignees: RequestWorker[]
	loadData: (showLoading?: boolean) => Promise<void>
	surveys: Survey[]
	surveysLoading: boolean
	allWorkItemsCount: number
}

export default function SurveysList({ 
	isExpanded, 
	onToggleExpand, 
	selectedSurveyId,
	onSurveyClick,
	filters,
	setFilters,
	assignees,
	loadData,
	surveys,
	surveysLoading,
	allWorkItemsCount
}: SurveysListProps) {
	const { projectTimeZone } = useProjectFeaturesContext()
	const { role } = useTokenClaims()
	
	const [selectMode, setSelectMode] = useState(false)
	const [selectedSurveys, setSelectedSurveys] = useState<number[]>([])
	const [assignDialogOpen, setAssignDialogOpen] = useState(false)
	const [viewMode, setViewMode] = useState<'pending' | 'history'>('pending')



	const handleViewChange = (event: React.SyntheticEvent, newValue: 'pending' | 'history') => {
		setViewMode(newValue)
		setFilters({ ...filters, viewMode: newValue })
	}

	const handleSurveySelect = React.useCallback((surveyId: number) => {
		setSelectedSurveys((prev) => {
			if (prev.includes(surveyId)) {
				return prev.filter((id) => id !== surveyId)
			}
			return [...prev, surveyId]
		})
	}, [])

	const handleAssign = async (assigneeId: number) => {
		await assignSurveys(selectedSurveys, assigneeId)
		setSelectedSurveys([])
		setSelectMode(false)
		await loadData(false)
	}

	if (surveysLoading) {
		return (
			<Paper
				sx={{
					p: 2,
					height: {
						xs: 'calc(100vh - 280px)',
						sm: 'calc(100vh - 260px)',
						md: 'calc(100vh - 230px)',
					},
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: '#f8f9fa',
				}}
			>
				<CircularProgress />
			</Paper>
		)
	}

	return (
		<>
			<Paper
				sx={{
					p: 2,
					height: {
						xs: role.title === "LineTech" ? 'calc(100vh - 230px)' : 'calc(100vh - 280px)',
						sm: role.title === "LineTech" ? 'calc(100vh - 210px)' : 'calc(100vh - 260px)',
						md: role.title === "LineTech" ? 'calc(100vh - 180px)' : 'calc(100vh - 230px)',
					},
					display: 'flex',
					flexDirection: 'column',
					backgroundColor: '#f8f9fa',
					position: 'relative',
					transition: 'all 0.3s ease',
					width: '100%',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 1,
						mb: 1,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<IconButton onClick={onToggleExpand}>
							<MenuIcon />
						</IconButton>
						{role.title !== "LineTech" && (
							<Box
								sx={{
									opacity: isExpanded ? 1 : 0,
									transition: 'opacity 0.2s ease',
								}}
							>
								<Button
									variant='outlined'
									size='medium'
									color='primary'
									sx={{
										color: 'primary.main',
										height: '2rem',
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
									}}
									onClick={() => {
										if (selectMode) {
											setSelectedSurveys([])
										}
										setSelectMode(!selectMode)
									}}
								>
									{selectMode ? 'Deselect' : 'Select'}
									{selectMode && selectedSurveys.length > 0 && (
										<Box
											sx={{
												backgroundColor: 'primary.main',
												color: 'white',
												borderRadius: '50%',
												minWidth: '20px',
												height: '20px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '0.75rem',
												fontWeight: 'bold',
											}}
										>
											{selectedSurveys.length}
										</Box>
									)}
								</Button>
							</Box>
						)}
						{role.title === "LineTech" && (
							<Box
								sx={{
									opacity: isExpanded ? 1 : 0,
									transition: 'opacity 0.2s ease',
								}}
							>
								<Tabs
									value={viewMode}
									onChange={handleViewChange}
									sx={{
										minHeight: '2rem',
										'& .MuiTab-root': {
											minHeight: '2rem',
											padding: '0 2rem',
											textTransform: 'none',
										},
									}}
								>
									<Tab
										value='pending'
										label='Pending'
										icon={<PendingIcon />}
										iconPosition='start'
									/>
									<Tab
										value='history'
										label='History'
										icon={<HistoryIcon />}
										iconPosition='start'
									/>
								</Tabs>
							</Box>
						)}
					</Box>
					{role.title !== 'LineTech' && selectedSurveys.length > 0 && (
						<Box
							sx={{
								opacity: isExpanded ? 1 : 0,
								transition: 'opacity 0.2s ease',
							}}
						>
							<Button
								variant='contained'
								color='primary'
								size='medium'
								startIcon={<AssignmentIcon />}
								sx={{
									color: 'white',
									height: '2rem',
								}}
								onClick={() => setAssignDialogOpen(true)}
							>
								Assign
							</Button>
						</Box>
					)}
				</Box>
				<Divider sx={{ mb: 1, opacity: isExpanded ? 1 : 0, transition: 'opacity 0.2s ease' }} />
				<Box
					sx={{
						flex: 1,
						overflow: 'auto',
						opacity: isExpanded ? 1 : 0,
						transition: 'opacity 0.2s ease',
						'&::-webkit-scrollbar': {
							width: '8px',
						},
						'&::-webkit-scrollbar-track': {
							background: '#f1f1f1',
							borderRadius: '4px',
						},
						'&::-webkit-scrollbar-thumb': {
							background: '#888',
							borderRadius: '4px',
							'&:hover': {
								background: '#555',
							},
						},
					}}
				>
					{surveys && surveys.length > 0 ? (
						<List>
							{surveys.map((survey) => (
								<SurveyListItem
									key={survey.id}
									survey={survey}
									selectMode={selectMode}
									isSelected={selectedSurveys.includes(survey.id)}
									onSelect={handleSurveySelect}
									projectTimeZone={projectTimeZone}
									selectedSurveyId={selectedSurveyId}
									onSurveyClick={onSurveyClick}
								/>
							))}
						</List>
					) : (
						<Typography variant='h6' color='text.secondary' align='center'>
							No surveys found.
						</Typography>
					)}
				</Box>
				<Box
					sx={{
						position: 'sticky',
						bottom: 0,
						py: 1,
						zIndex: 10,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						opacity: isExpanded ? 1 : 0,
						transition: 'opacity 0.2s ease',
					}}
				>
					<Pagination
						count={Math.ceil(allWorkItemsCount / 5)}
						variant='outlined'
						shape='rounded'
						size='large'
						onChange={(_, page) => setFilters({ ...filters, page: page - 1 })}
						page={filters.page + 1}
					/>
				</Box>
			</Paper>
			<AssignToDialog
				open={assignDialogOpen}
				onClose={() => setAssignDialogOpen(false)}
				assignees={assignees}
				selectedCount={selectedSurveys.length}
				onAssign={handleAssign}
				selectedItems={surveys?.filter((survey) => selectedSurveys.includes(survey.id)) || []}
			/>
		</>
	)
}
